import { useRouter, useRouterState } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";

import { AuthLoading } from "@/lib/auth/auth-provider";
import {
  isAuthPath,
  NO_ACCESS_PATH,
  safeReturnPath,
  SIGN_IN_PATH,
  signInUrl,
} from "@/lib/auth/config";
import { type AuthStatus, useAuthState } from "@/lib/auth/context";

interface GateLocation {
  /** Where the URL points (updates as soon as a navigation starts). */
  pathname: string;
  href: string;
  redirectParam: string | null;
  /** The path of the matches the Outlet renders right now (lags during a transition). */
  renderedPath: string;
}

type Screen = "sign-in" | "no-access" | "app";

function screenOf(pathname: string): Screen {
  if (pathname === SIGN_IN_PATH || pathname.startsWith(`${SIGN_IN_PATH}/`)) return "sign-in";
  if (pathname === NO_ACCESS_PATH) return "no-access";
  return "app";
}

/** The one screen each state may show. */
const ALLOWED: Record<AuthStatus, Screen | null> = {
  loading: null,
  "signed-out": "sign-in",
  "no-org": "no-access",
  ready: "app",
};

/**
 * The sign-in and organization gate (`docs/design/ia.md` §5, `flows.md` §1). With auth off it
 * renders through. Otherwise: signed out → `/sign-in?redirect_url=…`; signed in with no
 * organization → `/no-access`; ready → the screen (and the two auth screens bounce back to the
 * original URL). Redirects replace the history entry, so Back never lands on a gate screen.
 *
 * Children render only while the Outlet's *resolved* matches are the allowed screen: during a
 * route transition the URL moves first and the old screen stays mounted, and a protected screen
 * must not keep firing requests while the redirect to sign-in resolves.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const auth = useAuthState();
  const router = useRouter();
  const { pathname, href, redirectParam, renderedPath } = useRouterState({
    select: (state): GateLocation => {
      const search = state.location.search as Record<string, unknown>;
      const redirect = search.redirect_url;
      return {
        pathname: state.location.pathname,
        href: state.location.href,
        redirectParam: typeof redirect === "string" ? redirect : null,
        renderedPath: state.matches.at(-1)?.pathname ?? state.location.pathname,
      };
    },
  });

  const allowed = ALLOWED[auth.status];
  let target: string | null = null;
  if (auth.enabled && allowed && screenOf(pathname) !== allowed) {
    if (allowed === "sign-in") target = signInUrl(isAuthPath(pathname) ? null : href);
    else if (allowed === "no-access") target = NO_ACCESS_PATH;
    else target = safeReturnPath(redirectParam) ?? "/";
  }

  useEffect(() => {
    if (target) router.history.replace(target);
  }, [router, target]);

  if (!auth.enabled) return children;
  if (!allowed || target || screenOf(renderedPath) !== allowed) return <AuthLoading />;
  return children;
}
